import { IMAGE_WIDTHS, variantWidth } from "./image-widths";

/**
 * Image URLs on the CDN. See docs/architecture/storage-service.md, Serving.
 * The format is chosen by the CDN from `Accept`, so one URL per width serves
 * AVIF, WebP or JPEG as the browser allows.
 */

export type ImageSources = {
	/** For browsers that ignore `srcset`: twice the display width, for sharp phone screens. */
	src: string;
	/** One entry per allowed width from the display width to three times it. */
	srcSet: string;
	/** The display width in CSS pixels, for `sizes`. */
	width: number;
	/** Blurhash to show until the image loads, when the file has one. */
	placeholder: string | null;
};

/** `https://cdn.<root>/f/<fileId>/w<width>` with the width rounded up to an allowed one. */
export function imageUrl(cdnUrl: string, fileId: string, width: number): string {
	return `${cdnUrl.replace(/\/$/, "")}/f/${fileId}/w${variantWidth(width)}`;
}

/** `src`, `srcSet` and placeholder for an image shown `displayWidth` CSS pixels wide. */
export function imageSources(
	cdnUrl: string,
	file: { id: string; blurhash?: string | null },
	displayWidth: number,
): ImageSources {
	const widths = [
		...new Set([1, 1.5, 2, 3].map((density) => variantWidth(Math.ceil(displayWidth * density)))),
	].filter((w) => (IMAGE_WIDTHS as readonly number[]).includes(w));
	return {
		src: imageUrl(cdnUrl, file.id, displayWidth * 2),
		srcSet: widths.map((w) => `${imageUrl(cdnUrl, file.id, w)} ${w}w`).join(", "),
		width: displayWidth,
		placeholder: file.blurhash ?? null,
	};
}

/** What went wrong with an upload, for a message the person understands. */
export type UploadErrorCode =
	| "type"
	| "size"
	| "quota"
	| "forbidden"
	| "rate_limited"
	| "expired"
	| "unreadable"
	| "network"
	| "cancelled"
	| "failed";

/** The storage service's refusals (`POST /upload`) as upload error codes. */
export function uploadErrorFromStorage(status: number, error?: string): UploadErrorCode {
	switch (error) {
		case "TYPE_NOT_ALLOWED":
			return "type";
		case "UNREADABLE":
			return "unreadable";
		case "TOO_LARGE":
			return "size";
		case "TOKEN_EXPIRED":
			return "expired";
		// The service has too many large uploads in memory (P12-T07).
		case "BUSY":
			return "rate_limited";
		default:
			return status === 0 ? "network" : "failed";
	}
}

export class UploadError extends Error {
	readonly code: UploadErrorCode;
	constructor(code: UploadErrorCode) {
		super(`Upload failed: ${code}`);
		this.name = "UploadError";
		this.code = code;
	}
}

/** The storage service's answer to a stored upload. */
export type StoredUpload = {
	fileId: string;
	mime: string;
	sizeBytes: number;
	width: number | null;
	height: number | null;
	blurhash: string | null;
};
